import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useImportedTradesRecords } from './useImportedTrades';
import { useDemoTradesOverride } from '../demo/demoMode';
import {
  getPersistedTradeHistoryStatus,
  loadPersistedCsvTrades,
  mergePersistedCsvTradesWithLocalTrades,
  PERSISTED_TRADE_HISTORY_RETRY_EVENT,
} from '../lib/import-engine/persistedCsvTradeRead';
import { normalizeVisibleTradeIds } from '../lib/trades/visibleTradeIds';
import { filterTradesForTenantDisplay } from '../lib/trades/tenantTradeScope';
import { IMPORTED_TRADES_UPDATED_EVENT } from '../utils/importedTradesStorage';
import { privateTradingScopeChangedEventName } from '../lib/storage/privateTradingStorage';
import {
  CUSTOM_TRADES_UPDATED_EVENT,
  loadCustomTrades,
  sortTradesNewestFirst,
} from '../lib/trades/authoritativeTradeData';

/**
 * The single canonical authoritative trade collection.
 *
 * Every trade-driven page (Trades, Calendar, Journal, Trends, Dashboard) and
 * every trade-derived widget consumes this same hook so a mutation in any one
 * consumer (import, clear, delete, manual create/edit, broker sync, demo
 * load/removal) is reflected everywhere through the same refresh path.
 *
 * Sources (never merged with demo trades):
 *   1. local imported trades (trading.importedTrades.v2)
 *   2. server persisted CSV trades
 *   3. custom/journal trades (journal.customTrades.v1)
 *
 * Demo behavior (product semantics):
 *   - The demo override is shown ONLY when no real trades exist (real imported /
 *     persisted / custom trades always win over a stale demo override).
 *   - When demo mode is removed, the real collection is returned unchanged and
 *     no demo trade survives.
 */
export function useAuthoritativeTradesRecords() {
  const demoOverride = useDemoTradesOverride();
  const importedTrades = useImportedTradesRecords();
  const [customTrades, setCustomTrades] = useState(() => loadCustomTrades());
  const [persistedTrades, setPersistedTrades] = useState<any[]>([]);
  const [persistedReadVerified, setPersistedReadVerified] = useState(false);

  // Coalesce concurrent refreshes so a burst of rapid mutations does not fire a
  // request storm: only the last pending refresh wins, and only one request is
  // in flight at a time.
  const persistedRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistedRefreshGenerationRef = useRef(0);

  const refreshPersisted = useCallback((force = false, resume = false) => {
    const refreshGeneration = ++persistedRefreshGenerationRef.current;
    void loadPersistedCsvTrades({
      forceRefresh: force,
      resume,
    })
      .then((trades) => {
        if (refreshGeneration === persistedRefreshGenerationRef.current) {
          setPersistedTrades(Array.isArray(trades) ? trades : []);
          setPersistedReadVerified(true);
        }
      })
      .catch(() => {
        if (refreshGeneration === persistedRefreshGenerationRef.current) {
          setPersistedTrades([]);
          setPersistedReadVerified(false);
        }
      });
  }, []);

  const schedulePersistedRefresh = useCallback((force: boolean) => {
    if (persistedRefreshTimerRef.current != null) {
      clearTimeout(persistedRefreshTimerRef.current);
    }
    persistedRefreshTimerRef.current = setTimeout(() => {
      persistedRefreshTimerRef.current = null;
      refreshPersisted(force);
    }, 60);
  }, [refreshPersisted]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const refreshCustom = () => setCustomTrades(loadCustomTrades());
    const refreshAll = () => {
      refreshCustom();
      schedulePersistedRefresh(false);
    };
    const refreshAllForced = () => {
      refreshCustom();
      schedulePersistedRefresh(true);
    };
    const handleStorage = (event: StorageEvent) => {
      if (!event.key || event.key.startsWith('trade-proof:private:v1:local:') && (event.key.endsWith(':custom-trades') || event.key.endsWith(':imported-trades'))) {
        refreshAllForced();
      }
    };

    refreshCustom();
    refreshPersisted(false);
    const handleTenantContextChanged = () => {
      refreshCustom();
      refreshPersisted(true);
    };
    const handlePrivateScopeChanged = () => {
      persistedRefreshGenerationRef.current += 1;
      setCustomTrades([]);
      setPersistedTrades([]);
      setPersistedReadVerified(false);
      refreshCustom();
      refreshPersisted(true);
    };

    const handleRetry = () => refreshPersisted(true, true);

    window.addEventListener(IMPORTED_TRADES_UPDATED_EVENT, refreshAllForced as EventListener);
    window.addEventListener(CUSTOM_TRADES_UPDATED_EVENT, refreshCustom as EventListener);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', refreshAll as EventListener);
    window.addEventListener('trade-proof-tenant-context-changed', handleTenantContextChanged);
    window.addEventListener(privateTradingScopeChangedEventName(), handlePrivateScopeChanged);
    window.addEventListener(PERSISTED_TRADE_HISTORY_RETRY_EVENT, handleRetry);

    return () => {
      if (persistedRefreshTimerRef.current != null) {
        clearTimeout(persistedRefreshTimerRef.current);
      }
      window.removeEventListener(IMPORTED_TRADES_UPDATED_EVENT, refreshAllForced as EventListener);
      window.removeEventListener(CUSTOM_TRADES_UPDATED_EVENT, refreshCustom as EventListener);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', refreshAll as EventListener);
      window.removeEventListener('trade-proof-tenant-context-changed', handleTenantContextChanged);
      window.removeEventListener(privateTradingScopeChangedEventName(), handlePrivateScopeChanged);
      window.removeEventListener(PERSISTED_TRADE_HISTORY_RETRY_EVENT, handleRetry);
    };
  }, [refreshPersisted, schedulePersistedRefresh]);

  return useMemo(() => {
    const mergedLocalTrades = persistedReadVerified
      ? [...(importedTrades as any[]), ...customTrades]
      : (importedTrades as any[]);
    const mergedImportedTrades = mergePersistedCsvTradesWithLocalTrades(
      mergedLocalTrades,
      persistedTrades,
      { persistedReadVerified }
    );
    const mergedTrades = sortTradesNewestFirst([
      ...(mergedImportedTrades as any[]),
      ...(!persistedReadVerified ? customTrades : []),
    ]);
    // Boundary 2 — canonical tenant entitlement gate. Every trade-driven page
    // (Trades, Calendar, Journal, Trends, Dashboard) and every widget consumes
    // this hook, so the tenant-visible collection is filtered here once.
    // Stale/legacy records whose asset class is not enabled for the current
    // tenant (e.g. Futures/Forex/Crypto under a stocks-only tenant) can never
    // leak into any downstream consumer.
    const realTrades = filterTradesForTenantDisplay(mergedTrades);
    if (realTrades.length > 0) {
      // The COMPLETE final collection (imported + server-persisted + custom trades)
      // is normalized to the account-level T1..Tn sequence before React state reads
      // it. Custom trades are appended after the merge, so this final pass guarantees
      // a gapless sequence even when multiple sources contribute records.
      const historyTotal = getPersistedTradeHistoryStatus().total;
      const normalized = normalizeVisibleTradeIds(realTrades, { total: historyTotal });
      return normalized.records;
    }
    if (demoOverride?.enabled && Array.isArray(demoOverride.trades) && demoOverride.trades.length > 0) {
      return filterTradesForTenantDisplay(demoOverride.trades);
    }
    return [];
  }, [customTrades, demoOverride, importedTrades, persistedReadVerified, persistedTrades]);
}
